/**
 * Phase SC-21d18 — SA tenant-default seat-slot (shift) API client.
 *
 * Cross-tenant counterpart of the IA `/api/seating/slots` client. The super
 * admin always writes; the BE re-checks the `super_admin` role. New rows
 * from this UI are tenant defaults (`branch_id: null`). Branch rows may
 * still appear in the list and stay labelled as such.
 *
 *   GET    /super-admin/tenants/{slug}/seating/slots
 *   POST   /super-admin/tenants/{slug}/seating/slots
 *   PATCH  /super-admin/tenants/{slug}/seating/slots/{slot_id}
 *
 * PATCH must not send `key` — the BE request is `extra="forbid"` and the
 * key is immutable once bookings store it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type SeatSlotWeekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const WEEKDAYS: readonly SeatSlotWeekday[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export const WEEKDAY_SHORT: Record<SeatSlotWeekday, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

/** Matches the BE `key` pattern. */
export const SLOT_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export interface SeatSlot {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  key: string;
  label: string;
  /** `HH:MM:SS`, centre-local. */
  start_time: string;
  end_time: string;
  spans_midnight: boolean;
  days_of_week: SeatSlotWeekday[];
  effective_from: string | null;
  effective_until: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SeatSlotListResponse {
  items: SeatSlot[];
  total: number;
}

export interface CreateSeatSlotInput {
  branch_id?: string | null;
  key: string;
  label: string;
  start_time: string;
  end_time: string;
  spans_midnight: boolean;
  days_of_week: SeatSlotWeekday[];
  effective_from?: string | null;
  effective_until?: string | null;
  display_order: number;
  is_active?: boolean;
}

export type UpdateSeatSlotInput = Partial<Omit<CreateSeatSlotInput, "key">>;

export function seatSlotKeys(tenantSlug: string) {
  return ["super-admin", "seating", "slots", tenantSlug] as const;
}

export function seatSlotsPath(tenantSlug: string): string {
  return `/super-admin/tenants/${encodeURIComponent(tenantSlug)}/seating/slots`;
}

export function useSeatSlots(tenantSlug: string) {
  return useQuery<SeatSlotListResponse>({
    queryKey: seatSlotKeys(tenantSlug),
    queryFn: () => api.get<SeatSlotListResponse>(seatSlotsPath(tenantSlug)),
    enabled: Boolean(tenantSlug),
    staleTime: 15_000,
  });
}

export function useCreateSeatSlot(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation<SeatSlot, ApiError, CreateSeatSlotInput>({
    mutationFn: (body) => api.post<SeatSlot>(seatSlotsPath(tenantSlug), body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: seatSlotKeys(tenantSlug) }),
  });
}

export function useUpdateSeatSlot(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation<SeatSlot, ApiError, { id: string; body: UpdateSeatSlotInput }>({
    mutationFn: ({ id, body }) =>
      api.patch<SeatSlot>(`${seatSlotsPath(tenantSlug)}/${encodeURIComponent(id)}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: seatSlotKeys(tenantSlug) }),
  });
}

/** Live list order: active first, then the learner-facing `display_order`. */
export function sortSlots(slots: SeatSlot[]): SeatSlot[] {
  return [...slots].sort(
    (a, b) =>
      Number(b.is_active) - Number(a.is_active) ||
      a.display_order - b.display_order ||
      a.key.localeCompare(b.key),
  );
}

/** `09:00:00` → `09:00` for `<input type="time">`. */
export function toTimeInput(value: string): string {
  return value.slice(0, 5);
}

/** `14:30:00` → `2:30 PM`. */
export function formatClock(value: string): string {
  const [hh = "0", mm = "00"] = value.split(":");
  const h = Number(hh);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${suffix}`;
}

/** `Mon–Fri`, `Every day`, or `Mon, Wed, Sat`. */
export function formatDays(days: readonly SeatSlotWeekday[]): string {
  const set = new Set(days);
  if (set.size === 7) return "Every day";
  const ordered = WEEKDAYS.filter((d) => set.has(d));
  const weekdays = WEEKDAYS.slice(0, 5);
  if (ordered.length === 5 && weekdays.every((d) => set.has(d))) return "Mon–Fri";
  return ordered.map((d) => WEEKDAY_SHORT[d]).join(", ");
}
